import { useEffect, useState, useRef } from 'react';
import { ApolloProvider } from '@apollo/client';
import { useSession } from 'next-auth/react';
import { initApollo } from './initApollo';
import { logout } from '../auth';
import Toastr from '../components/Toastr';

const config = require('@cardash/config').default || require('@cardash/config');
const { ADMIN_APP_MAX_RANK } = config;

/**
 * Checks if a user's rank is allowed to access the admin app.
 * @param {number|undefined|null} rank - The user's rank
 * @returns {boolean} - True if allowed, false if denied
 */
const isRankAllowed = rank => {
  if (rank == null || typeof rank !== 'number') {
    return false;
  }
  if (typeof ADMIN_APP_MAX_RANK !== 'number') {
    return false;
  }
  return rank <= ADMIN_APP_MAX_RANK;
};

export const WithApolloClient = ({ children }) => {
  const { data: session } = useSession();
  const [apolloClient, setApolloClient] = useState(initApollo());
  const [isAccessDenied, setIsAccessDenied] = useState(false);

  // Refs to handle StrictMode double-render and prevent duplicate actions
  const toastShownRef = useRef(false);
  const timeoutIdRef = useRef(null);

  // Rank check effect
  useEffect(() => {
    // Skip if session not loaded yet or user not authenticated
    if (!session?.user) {
      return;
    }

    const currentRank = session.user.rank;

    // Check if rank is not allowed (handles undefined, null, and rank > threshold)
    if (!isRankAllowed(currentRank)) {
      setIsAccessDenied(true);

      // Show toast only once (Toastr also has preventDuplicates, but this is explicit)
      if (!toastShownRef.current) {
        toastShownRef.current = true;
        Toastr.warning('Your role does not have access to this application. You will be logged out in 10 seconds.');
      }

      // Start timeout only if not already running (handles StrictMode re-runs)
      if (timeoutIdRef.current === null) {
        timeoutIdRef.current = setTimeout(async () => {
          timeoutIdRef.current = null;
          try {
            await logout();
          } catch (error) {
            // Log error but continue with redirect
            //eslint-disable-next-line no-console
            console.error('Logout failed:', error);
          }
          window.location.href = '/login?error=AccessDenied';
        }, 10000);
      }

      // Cleanup: clear timeout on unmount or dependency change
      return () => {
        if (timeoutIdRef.current !== null) {
          clearTimeout(timeoutIdRef.current);
          timeoutIdRef.current = null;
        }
      };
    }
  }, [session?.user]);

  // Initialize Apollo client when session changes (only for authorized users)
  useEffect(() => {
    // Only set up authenticated Apollo client for authorized users
    if (session?.user && isRankAllowed(session.user.rank)) {
      const authTokens = {
        jwtToken: session.user.jwtToken,
        csrfToken: session.user.csrfToken,
        refreshToken: session.user.refreshToken,
      };
      setApolloClient(initApollo({}, authTokens));
    }
  }, [session?.user]);

  // Show access denied screen while waiting for logout
  if (isAccessDenied) {
    return <div>Access Denied - Logging out...</div>;
  }

  if (!apolloClient) {
    return <div>Loading...</div>;
  }

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};
